import { supabase } from "@/integrations/supabase/client";
import { formatOrderReceipt, formatCustomerReceipt, PrintJob, ensureQZConnected, printReceipt, isQZWebSocketActive, getPrinterSettings, formatConferenceBill } from "@/lib/qz-tray";
import { getPrinterNameForJob } from "@/lib/print-routing";
import { addMultipleToPrintQueue } from "@/lib/print-queue";
import { dispatchPrint, dispatchMultiplePrints } from "@/lib/print-dispatcher";
import { toast } from "sonner";
import type { JobType } from "@/hooks/usePrinters";
import { fetchPrintTemplate } from "@/hooks/usePrintTemplates";

// Helper function to extract product IDs from combo_selections
function extractProductIdsFromComboSelections(comboSelections: any): string[] {
  const productIds: string[] = [];
  
  if (!comboSelections) return productIds;
  
  // Handle slotSelections format (tablet/modern)
  const slotSelections = comboSelections.slotSelections;
  if (slotSelections) {
    // Handle object format (tablet) - keys are slot IDs, values are arrays of products
    if (!Array.isArray(slotSelections)) {
      for (const products of Object.values(slotSelections)) {
        if (Array.isArray(products)) {
          for (const product of products as any[]) {
            if (product.productId) productIds.push(product.productId);
          }
        }
      }
    } else {
      // Handle array format (legacy)
      for (const slot of slotSelections) {
        if (slot.selectedProducts) {
          for (const product of slot.selectedProducts) {
            if (product.productId) productIds.push(product.productId);
          }
        }
      }
    }
  }

  // Handle pizza flavors format (legacy PDV)
  if (comboSelections.pizzaFlavors && Array.isArray(comboSelections.pizzaFlavors)) {
    for (const flavor of comboSelections.pizzaFlavors) {
      if (flavor.productId) productIds.push(flavor.productId);
    }
  }

  // Handle choices format (legacy PDV combos)
  if (comboSelections.choices && Array.isArray(comboSelections.choices)) {
    for (const choice of comboSelections.choices) {
      if (choice.productId) productIds.push(choice.productId);
    }
  }
  
  return productIds;
}

interface OrderItem {
  product_id: string;
  product_name: string;
  quantity: number;
  notes?: string | null;
  selected_addons?: Array<{ name: string; price: number }> | null;
  combo_id?: string | null;
  combo_selections?: any;
}

interface OrderInfo {
  type: string;
  tableNumber?: string;
  tabNumber?: string;
  customerName?: string;
  waiterName?: string;
  notes?: string;
  isDelivery?: boolean;
  deliveryAddress?: string;
  customerPhone?: string;
  sourceName?: string;
}

interface ProductLocation {
  id: string;
  production_location_id: string | null;
  production_locations: {
    id: string;
    name: string;
    printer_name: string;
    printer_id: string | null;
    job_type: string | null;
    printers: { printer_name: string } | null;
  } | null;
}

export async function printOrderByLocations(
  orderNumber: number,
  items: OrderItem[],
  orderInfo: OrderInfo,
  restaurantId: string
): Promise<void> {
  // Print mode is now handled by the dispatcher (supports QZ Tray and RawBT)
  
  // Get products and combos with their production locations
  const productIds = items.map(item => item.product_id).filter(Boolean);
  const comboIds = items.map(item => item.combo_id).filter(Boolean);

  // Extract product IDs from combo selections for "inherit from product" mode
  const comboProductIds: string[] = [];
  for (const item of items) {
    if (item.combo_id && item.combo_selections) {
      const ids = extractProductIdsFromComboSelections(item.combo_selections);
      comboProductIds.push(...ids);
    }
  }

  // Merge all product IDs for fetching
  const allProductIds = [...new Set([...productIds, ...comboProductIds])];

  if (allProductIds.length === 0 && comboIds.length === 0) {
    console.warn("[PRINT] Nenhum item para imprimir");
    return;
  }

  // Fetch product locations (including products from combo selections)
  let products: any[] = [];
  if (allProductIds.length > 0) {
    const { data, error } = await supabase
      .from("products")
      .select("id, production_location_id, production_locations(id, name, printer_name, printer_id, job_type, printers(printer_name))")
      .in("id", allProductIds)
      .eq("restaurant_id", restaurantId);

    if (error) {
      console.error("Error fetching product locations:", error);
    } else if (data) {
      products = data;
    }
  }

  // Fetch combo locations
  let combos: any[] = [];
  if (comboIds.length > 0) {
    const { data, error } = await supabase
      .from("combos")
      .select("id, production_location_id, production_locations(id, name, printer_name, printer_id, job_type, printers(printer_name))")
      .in("id", comboIds)
      .eq("restaurant_id", restaurantId);

    if (error) {
      console.error("Error fetching combo locations:", error);
    } else if (data) {
      combos = data;
    }
  }

  // Group items by production location
  const locationGroups: Map<string, { 
    locationName: string; 
    printerName: string;
    jobType: JobType | null;
    items: OrderItem[] 
  }> = new Map();

  for (const item of items) {
    let locationData = null;

    // Check if it's a combo first
    if (item.combo_id) {
      const combo = combos.find((c: any) => c.id === item.combo_id);
      
      // If combo has direct production location, use it
      if (combo?.production_locations) {
        locationData = combo.production_locations;
      } else {
        // Combo is in "inherit from product" mode - get location from first selected product
        const selectedProductIds = extractProductIdsFromComboSelections(item.combo_selections);
        
        if (selectedProductIds.length > 0) {
          // Use the production location of the first selected product
          const firstProduct = products.find((p: any) => selectedProductIds.includes(p.id));
          if (firstProduct?.production_locations) {
            locationData = firstProduct.production_locations;
            console.log(`[PRINT] Combo "${item.product_name}" herdando local de produção do produto: ${firstProduct.id}`);
          }
        }
      }
    }

    // Check if it's a regular product (only if not already resolved from combo)
    if (!locationData && item.product_id) {
      const product = products.find((p: any) => p.id === item.product_id);
      if (product?.production_locations) {
        locationData = product.production_locations;
      }
    }

    if (locationData) {
      const locationId = locationData.id;
      const locationName = locationData.name;
      // Prefer printer from printer_id reference, fallback to legacy printer_name
      const printerName = locationData.printers?.printer_name || locationData.printer_name;
      const jobType = locationData.job_type as JobType | null;
      
      if (!locationGroups.has(locationId)) {
        locationGroups.set(locationId, { locationName, printerName, jobType, items: [] });
      }
      locationGroups.get(locationId)!.items.push(item);
    } else if (item.combo_id) {
      console.warn(`[PRINT] Combo "${item.product_name}" sem local de produção configurado (nem próprio, nem herdado)`);
    }
  }

  // Create print jobs - resolve printer for each location
  const printJobs: PrintJob[] = [];

  for (const [, group] of locationGroups) {
    // Try to get printer from job type configuration first
    let resolvedPrinter: string | null = null;
    
    if (group.jobType) {
      resolvedPrinter = await getPrinterNameForJob(restaurantId, group.jobType, false);
    }
    
    // Fallback to the direct printer_name from production location
    const finalPrinterName = resolvedPrinter || group.printerName;
    
    if (!finalPrinterName) {
      console.warn(`No printer configured for location: ${group.locationName}`);
      continue;
    }

    // Get printer settings for chars_per_line
    const printerSettings = await getPrinterSettings(finalPrinterName, restaurantId);
    const charsPerLine = printerSettings?.chars_per_line || 48;

    const content = formatOrderReceipt(
      orderNumber,
      group.items.map(item => ({
        name: item.product_name,
        quantity: item.quantity,
        notes: item.notes || undefined,
        selectedAddons: item.selected_addons || undefined,
        comboId: item.combo_id || undefined,
        comboSelections: item.combo_selections || undefined,
      })),
      group.locationName,
      orderInfo.type,
      orderInfo.type === "table" ? orderInfo.tableNumber : orderInfo.tabNumber,
      orderInfo.customerName,
      orderInfo.waiterName,
      orderInfo.notes,
      charsPerLine,
      orderInfo.isDelivery ? {
        isDelivery: true,
        deliveryAddress: orderInfo.deliveryAddress,
        customerPhone: orderInfo.customerPhone,
      } : undefined,
      orderInfo.sourceName
    );

    printJobs.push({ 
      printerName: finalPrinterName, 
      content,
      logType: "pedidos",
      logIdentifier: orderNumber,
      locationName: group.locationName,
      settings: printerSettings || undefined
    });
  }

  if (printJobs.length > 0) {
    const success = await dispatchMultiplePrints(restaurantId, printJobs, orderNumber);
    if (success) {
      toast.success(`Pedido enviado para ${printJobs.length} impressora(s)`);
    } else {
      // Fallback to local queue if dispatch fails
      addMultipleToPrintQueue(printJobs, orderNumber, "Falha no dispatch");
      toast.warning("Impressão na fila local", {
        description: `Pedido #${orderNumber} será impresso quando reconectar`
      });
    }
  }
}

interface CustomerReceiptData {
  orderNumber: number;
  items: {
    name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
  }[];
  subtotal: number;
  serviceTax?: number;
  discount?: number;
  total: number;
  paymentMethod: string;
  amountReceived?: number;
  change?: number;
  customerName?: string;
  restaurantName?: string;
  restaurantAddress?: string;
  restaurantId?: string;
  // Entry (entrada de comanda)
  entryTypeName?: string;
  entryAmount?: number;
  entryMode?: "fixed" | "minimum_consumption";
  entryDifference?: number;
  // Couvert
  couvertAmount?: number;
  couvertQuantity?: number;
}

export async function printCustomerReceipt(data: CustomerReceiptData): Promise<void> {
  const connected = await ensureQZConnected();
  if (!connected) {
    console.log("[PRINT] QZ Tray não conectado - via do cliente ignorada");
    return;
  }

  try {
    // Get printer from routing
    const printerName = await getPrinterNameForJob(data.restaurantId || "", "customer_receipt") || "default";
    
    // Get printer settings for chars_per_line
    const printerSettings = await getPrinterSettings(printerName, data.restaurantId || "");
    const charsPerLine = printerSettings?.chars_per_line || 48;
    
    // Fetch print template
    const template = await fetchPrintTemplate(data.restaurantId || "", "customer_receipt");
    
    const content = formatCustomerReceipt(data, charsPerLine, template);
    
    await printReceipt(printerName, content, {
      logType: "cupons",
      logIdentifier: data.orderNumber,
      settings: printerSettings || undefined
    });
    toast.success("Via do cliente impressa!");
  } catch (error) {
    console.error("Error printing customer receipt:", error);
    // Don't show error toast - printing is optional
  }
}

// Conference Bill Printing (Conferência de Conta)
interface ConferenceBillPrintData {
  orderId: string;
  restaurantId: string;
  tableNumber?: string;
  tabNumber?: string;
}

export async function printConferenceBill(
  restaurantId: string,
  orderId?: string,
  tableId?: string,
  commandId?: string
): Promise<boolean> {
  // Quick check if QZ Tray is already connected (no timeout, instant)
  const connected = isQZWebSocketActive();

  try {
    // Get restaurant data
    const { data: restaurant } = await supabase
      .from("restaurants")
      .select("name, address, service_tax_percentage, service_tax_enabled")
      .eq("id", restaurantId)
      .single();

    // Find the order - either by orderId directly or by table/command
    let orderQuery = supabase
      .from("orders")
      .select("*, order_items(*)")
      .eq("restaurant_id", restaurantId)
      .in("status", ["pending", "preparing", "ready"]);

    let commandNumber: string | null = null;

    if (orderId) {
      orderQuery = orderQuery.eq("id", orderId);
    } else if (tableId) {
      // Get table's current order (or grouped order)
      const { data: table } = await supabase
        .from("tables")
        .select("current_order_id, number, service_tax_enabled, grouped_to_order_id")
        .eq("id", tableId)
        .single();
      
      const tableOrderId = table?.current_order_id || table?.grouped_to_order_id;
      if (!tableOrderId) {
        toast.warning("Não há pedido aberto para esta mesa");
        return false;
      }
      orderQuery = orderQuery.eq("id", tableOrderId);
    } else if (commandId) {
      // Get command's current order (or grouped order)
      const { data: command } = await supabase
        .from("commands")
        .select("current_order_id, number, customer_name, service_tax_enabled, grouped_to_order_id")
        .eq("id", commandId)
        .single();
      
      const commandOrderId = command?.current_order_id || command?.grouped_to_order_id;
      if (!commandOrderId) {
        toast.warning("Não há pedido aberto para esta comanda");
        return false;
      }
      orderQuery = orderQuery.eq("id", commandOrderId);
      commandNumber = command.number;
    } else {
      toast.error("Selecione uma mesa ou comanda");
      return false;
    }

    const { data: order, error } = await orderQuery.single();

    if (error || !order) {
      toast.warning("Pedido não encontrado");
      return false;
    }

    // Get command number from order if not already set
    if (!commandNumber && order.tab_number) {
      commandNumber = order.tab_number;
    }

    // Fetch command entry if command number exists
    let entryData: { 
      entryTypeName?: string; 
      entryAmount?: number; 
      entryMode?: "fixed" | "minimum_consumption";
      entryDifference?: number;
    } = {};

    if (commandNumber) {
      const { data: entry } = await supabase
        .from("command_entries")
        .select("entry_type_name, amount, entry_mode, status")
        .eq("restaurant_id", restaurantId)
        .eq("command_number", commandNumber)
        .eq("status", "active")
        .maybeSingle();

      if (entry) {
        entryData.entryTypeName = entry.entry_type_name;
        entryData.entryAmount = entry.amount;
        entryData.entryMode = entry.entry_mode as "fixed" | "minimum_consumption";
        
        // Calculate entry difference for minimum consumption
        if (entry.entry_mode === "minimum_consumption") {
          const consumedAmount = order.subtotal;
          const minimumAmount = entry.amount;
          if (consumedAmount < minimumAmount) {
            entryData.entryDifference = minimumAmount - consumedAmount;
          }
        }
      }
    }

    // Calculate service tax if enabled
    let serviceTax = 0;
    let serviceTaxEnabled = restaurant?.service_tax_enabled ?? false;
    
    // Check table/command specific service tax setting
    if (tableId) {
      const { data: table } = await supabase
        .from("tables")
        .select("service_tax_enabled")
        .eq("id", tableId)
        .single();
      serviceTaxEnabled = table?.service_tax_enabled ?? serviceTaxEnabled;
    } else if (commandId) {
      const { data: command } = await supabase
        .from("commands")
        .select("service_tax_enabled")
        .eq("id", commandId)
        .single();
      serviceTaxEnabled = command?.service_tax_enabled ?? serviceTaxEnabled;
    }

    if (serviceTaxEnabled && restaurant?.service_tax_percentage) {
      serviceTax = order.subtotal * (restaurant.service_tax_percentage / 100);
    }

    const couvertTotal = (order.couvert_quantity || 0) * (order.couvert_amount || 0);
    // Entry adds to total: fixed mode adds full amount, minimum_consumption adds only the deficit
    const entryAmountToAdd =
      entryData.entryMode === "fixed"
        ? entryData.entryAmount || 0
        : entryData.entryDifference || 0;
    const total = order.subtotal + serviceTax + couvertTotal + entryAmountToAdd - (order.discount || 0);

    // Get printer from routing
    const printerName = await getPrinterNameForJob(restaurantId, "conference_bill") || "default";
    
    // Get printer settings for chars_per_line
    const printerSettings = await getPrinterSettings(printerName, restaurantId);
    const charsPerLine = printerSettings?.chars_per_line || 48;

    // Fetch print template for conference bill
    const template = await fetchPrintTemplate(restaurantId, "conference_bill");

    const content = formatConferenceBill({
      orderNumber: order.order_number,
      items: (order.order_items || []).map((item: any) => ({
        name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        notes: item.notes,
      })),
      subtotal: order.subtotal,
      serviceTax: serviceTax > 0 ? serviceTax : undefined,
      serviceTaxPercentage: serviceTaxEnabled ? restaurant?.service_tax_percentage : undefined,
      couvertAmount: couvertTotal > 0 ? couvertTotal : undefined,
      couvertQuantity: order.couvert_quantity || undefined,
      total,
      customerName: order.customer_name || undefined,
      tableNumber: order.table_number || undefined,
      tabNumber: order.tab_number || undefined,
      restaurantName: restaurant?.name,
      restaurantAddress: restaurant?.address,
      // Entry information
      entryTypeName: entryData.entryTypeName,
      entryAmount: entryData.entryAmount,
      entryMode: entryData.entryMode,
      entryDifference: entryData.entryDifference,
      peopleCount: order.people_count || undefined,
    }, charsPerLine, template);
    
    // Dispatch print via centralized dispatcher
    const success = await dispatchPrint(restaurantId, printerName, content, {
      logType: "conferencia",
      logIdentifier: order.order_number,
      locationName: "Conferência",
      orderNumber: order.order_number,
      settings: printerSettings || undefined,
    });

    if (success) {
      toast.success("Conferência enviada para impressão!");
    } else {
      toast.error("Erro ao enviar conferência para impressão");
    }
    return success;
    
  } catch (error) {
    console.error("Error printing conference bill:", error);
    toast.error("Erro ao imprimir conferência");
    return false;
  }
}
